import type { CompanyAddress } from "@/components/Forms/ProviderCompanyAutocomplete.tsx";
import { companySchema } from "@/components/Forms/schema/companySchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { createDataSelector, createResourceSelector, handleJsonApiError } from "jsonapi-zod-query";

const resourceSelector = createResourceSelector({
    type: "company",
    attributesSchema: companySchema,
});

export const companySelector = createDataSelector(resourceSelector);
export type NewCompany = ReturnType<typeof companySelector>;

export const useAddCompany = (): UseMutationResult<NewCompany, Error, CompanyAddress | null> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (companyInformation: CompanyAddress | null) => {
            const response = await fetch(apiUrl("/company/create"), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify(companyInformation ?? {}),
            });
            await handleJsonApiError(response);
            return await response.json();
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["companies"],
            });
        },
    });
};
